import { useCallback, useEffect, useState } from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "../../api";
import { io, Socket } from "socket.io-client";

export type InAppNotificationRow = {
  in_app_notification_id: number;
  type: string;
  title: string;
  body: string | null;
  payload: Record<string, unknown> | null;
  read_at: string | null;
  created_at: string;
};

const SOCKET_PATH = "/api/socket.io";

function getSocketBaseUrl() {
  if (typeof globalThis === "undefined" || !("location" in globalThis)) {
    return "";
  }
  const loc = (globalThis as { location: { host: string; protocol: string } })
    .location;
  if (loc.host === "localhost:3000") {
    return "http://localhost:5000";
  }
  return `${loc.protocol}//${loc.host}`;
}

export function useInAppSocket(
  onRefresh: () => void,
  user: {
    userId?: number;
    roleId?: number;
    isSuperAdmin?: boolean;
    Role?: string[];
  } | null
) {
  useEffect(() => {
    if (!user || user.isSuperAdmin) return;
    const base = getSocketBaseUrl();
    const s: Socket = io(base, {
      path: SOCKET_PATH,
      withCredentials: true,
      transports: ["websocket", "polling"],
    });
    const role = user.Role?.[0];
    if (["admin", "supervisor", "employee", "stockManager", "BranchHead"].includes(
      role || ""
    )) {
      s.emit("join_in_app", { employeeId: user.userId });
    } else if (role === "customer") {
      s.emit("join_in_app", { customerId: user.userId });
    }
    s.on("in_app_notification", () => onRefresh());
    return () => {
      s.removeAllListeners();
      s.close();
    };
  }, [user, onRefresh]);
}

type NotificationDrawerProps = Readonly<{
  open: boolean;
  onClose: () => void;
  /** Called when the drawer opens and after socket refresh (from parent) */
  refreshSignal?: number;
  onAfterRead?: () => void;
}>;

export default function NotificationDrawer({
  open,
  onClose,
  refreshSignal = 0,
  onAfterRead,
}: NotificationDrawerProps) {
  const user = useSelector((state: { auth?: { user?: unknown } }) => (state as { auth: { user?: { userId?: number; Role?: string[]; isSuperAdmin?: boolean } } }).auth?.user);
  const navigate = useNavigate();
  const [list, setList] = useState<InAppNotificationRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!user || user.isSuperAdmin) {
      setList([]);
      return;
    }
    setLoading(true);
    axios
      .get("/in-app-notifications")
      .then((r) => {
        setList(r.data?.notifications || []);
      })
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    if (open) load();
  }, [open, load, refreshSignal]);

  const onItemClick = async (n: InAppNotificationRow) => {
    const p = n.payload;
    const path =
      (p?.path as string) || (p?.link as string);
    if (!n.read_at) {
      try {
        await axios.put(
          `/in-app-notifications/${n.in_app_notification_id}/read`,
          {}
        );
        setList((prev) =>
          prev.map((x) =>
            x.in_app_notification_id === n.in_app_notification_id
              ? { ...x, read_at: new Date().toISOString() }
              : x
          )
        );
        onAfterRead?.();
      } catch {
        // ignore
      }
    }
    onClose();
    if (path) {
      navigate(path);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put("/in-app-notifications/read-all", {});
      load();
      onAfterRead?.();
    } catch {
      // ignore
    }
  };

  const clearAll = async () => {
    if (list.length === 0) return;
    try {
      await axios.delete("/in-app-notifications/all");
      setList([]);
      onAfterRead?.();
    } catch {
      // ignore
    }
  };

  if (!user || user.isSuperAdmin) {
    return null;
  }

  const textWrapSx = {
    whiteSpace: "normal" as const,
    overflow: "visible" as const,
    textOverflow: "clip" as const,
    wordBreak: "break-word" as const,
    overflowWrap: "anywhere" as const,
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 420 },
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          flexShrink: 0,
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={1}
        >
          <Typography variant="h6" sx={{ pr: 1, lineHeight: 1.3 }}>
            Notifications
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="close" sx={{ mt: -0.5 }}>
            <CloseIcon />
          </IconButton>
        </Stack>
        {list.length > 0 && (
          <Stack
            direction="row"
            flexWrap="wrap"
            spacing={1}
            sx={{ mt: 1.5 }}
            justifyContent="flex-end"
          >
            {list.some((n) => !n.read_at) && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={markAllRead}
                sx={(theme) => ({
                  textTransform: "none",
                  fontWeight: 600,
                  borderWidth: 1.5,
                  borderColor: alpha(theme.palette.primary.main, 0.55),
                  color: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  "&:hover": {
                    borderWidth: 1.5,
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                  },
                })}
              >
                Mark All As Read
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={clearAll}
              sx={(theme) => ({
                textTransform: "none",
                fontWeight: 700,
                borderWidth: 2,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.25)} inset`,
                "&:hover": {
                  borderWidth: 2,
                  borderColor: theme.palette.primary.dark,
                  color: theme.palette.primary.dark,
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                  boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.35)} inset`,
                },
              })}
            >
              Clear all
            </Button>
          </Stack>
        )}
      </Box>
      <Divider />
      {loading && (
        <Box sx={{ px: 2.5, py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      )}
      {!loading && list.length === 0 && (
        <Box sx={{ px: 2.5, py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No notifications yet.
          </Typography>
        </Box>
      )}
      <List
        dense
        sx={{
          pt: 0,
          px: 1.5,
          pb: 2,
          flex: 1,
          minHeight: 0,
          overflow: "auto",
        }}
      >
        {list.map((n) => (
          <ListItem
            key={n.in_app_notification_id}
            onClick={() => onItemClick(n)}
            alignItems="flex-start"
            sx={{
              cursor: "pointer",
              borderRadius: 1,
              px: 1.5,
              py: 1.5,
              mb: 0.5,
              minWidth: 0,
              bgcolor: n.read_at ? "transparent" : "action.hover",
              border: 1,
              borderColor: "divider",
            }}
          >
            <ListItemText
              primary={n.title}
              secondary={
                <Box component="div" display="block" sx={textWrapSx}>
                  {n.body}
                  {n.created_at && (
                    <Typography
                      component="div"
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.75, display: "block", ...textWrapSx }}
                    >
                      {new Date(n.created_at).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              }
              primaryTypographyProps={{
                noWrap: false,
                fontWeight: n.read_at ? 400 : 600,
                variant: "subtitle2",
                sx: { ...textWrapSx, mb: 0.5, lineHeight: 1.35 },
              }}
              secondaryTypographyProps={{
                noWrap: false,
                component: "div" as const,
                sx: { ...textWrapSx, mt: 0.25 },
              }}
            />
          </ListItem>
        ))}
      </List>
    </Drawer>
  );
}

export function useNotificationUnreadCount(
  onSocketEvent?: () => void
) {
  const user = useSelector((state: { auth?: { user?: { isSuperAdmin?: boolean; Role?: string[]; userId?: number } } }) => (state as { auth: { user?: { isSuperAdmin?: boolean; Role?: string[]; userId?: number } } }).auth?.user);
  const [count, setCount] = useState(0);

  const loadCountOnly = useCallback(() => {
    if (!user || user.isSuperAdmin) {
      setCount(0);
      return;
    }
    axios
      .get("/in-app-notifications/unread-count")
      .then((r) => setCount(r.data?.unreadCount || 0))
      .catch(() => setCount(0));
  }, [user]);

  useEffect(() => {
    loadCountOnly();
  }, [loadCountOnly]);

  const onSocket = useCallback(() => {
    loadCountOnly();
    onSocketEvent?.();
  }, [loadCountOnly, onSocketEvent]);

  useInAppSocket(onSocket, user ?? null);

  return { count, refresh: loadCountOnly };
}
